import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";
import { TileTypes } from "@/types/review.types";
import { useClipboard } from "./useClipboard";

export interface UseTileCopyOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

interface CopyContext {
  setIsCopying: (v: boolean) => void;
  setCopyingTileId: (v: string | null) => void;
  options?: UseTileCopyOptions;
}

const getSuggestedClause = (data: TileTypes): string | null => {
  if ("suggestedclause" in data && data.suggestedclause) return data.suggestedclause;
  if ("suggestion" in data && data.suggestion) return data.suggestion;
  if ("suggestions" in data && data.suggestions) return data.suggestions;
  if ("suggestedNumber" in data && data.suggestedNumber) return data.suggestedNumber;
  return null;
};

const formatExtraFieldsHtml = (data: TileTypes): string => {
  let html = "";
  if ("ambiguousText" in data && data.ambiguousText)
    html += `<p><strong>Ambiguous Text:</strong> ${data.ambiguousText}</p>`;
  if ("complexText" in data && data.complexText)
    html += `<p><strong>Complex Text:</strong> ${data.complexText}</p>`;
  if ("riskText" in data && data.riskText)
    html += `<p><strong>Risk Text:</strong> ${data.riskText}</p>`;
  if ("nonStandardText" in data && data.nonStandardText)
    html += `<p><strong>Non-Standard Text:</strong> ${data.nonStandardText}</p>`;
  if ("text1" in data && data.text1) html += `<p><strong>Text 1:</strong> ${data.text1}</p>`;
  if ("text2" in data && data.text2) html += `<p><strong>Text 2:</strong> ${data.text2}</p>`;
  return html;
};

const formatTileContentForCopy = (tile: ReviewTileItem): string => {
  let html = "";
  const originalData = tile.originalData;

  if (tile.clause != null) {
    html += `<p><strong>Clause:</strong> ${tile.clause}</p>`;
  }
  if (originalData != null) {
    html += formatExtraFieldsHtml(originalData);
  }
  if (tile.reason != null) {
    html += `<p><strong>Reason:</strong> ${tile.reason}</p>`;
  }
  if (tile.analysis != null) {
    html += `<p><strong>Analysis:</strong> ${tile.analysis}</p>`;
  }
  if (tile.explanation != null) {
    html += `<p><strong>Explanation:</strong> ${tile.explanation}</p>`;
  }
  if (originalData != null) {
    if ("riskExplanation" in originalData && originalData.riskExplanation) {
      html += `<p><strong>Risk Explanation:</strong> ${originalData.riskExplanation}</p>`;
    }
    const suggestedClause = getSuggestedClause(originalData);
    if (suggestedClause != null) {
      html += `<p><strong>Suggestion:</strong> ${suggestedClause}</p>`;
    }
  }
  return html;
};

const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent ?? "";
};

function buildTilesTableText(tiles: ReviewTileItem[]): string {
  let text = "Clause/Section\tImpact\tReason/Analysis\n";
  text += "---\t---\t---\n";
  tiles.forEach((tile) => {
    const clause = tile.clause ?? "N/A";
    const impact = tile.impact && tile.impact !== "ALL" ? tile.impact : "N/A";
    const reason = tile.reason ?? tile.analysis ?? tile.explanation ?? "N/A";
    text += `${clause}\t${impact}\t${reason}\n`;
  });
  return text;
}

function buildTilesJsonText(tiles: ReviewTileItem[]): string {
  return JSON.stringify(
    tiles.map((t) => t.originalData),
    null,
    2
  );
}

function buildTilesSummaryText(tiles: ReviewTileItem[]): string {
  const impactCounts = tiles.reduce(
    (acc, tile) => {
      const impact = tile.impact ?? "UNKNOWN";
      acc[impact] = (acc[impact] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  let text = `Review Summary (${tiles.length} items)\n`;
  text += "=".repeat(40) + "\n\n";
  text += "Impact Breakdown:\n";
  Object.entries(impactCounts).forEach(([impact, count]) => {
    if (impact !== "ALL") {
      text += `- ${impact}: ${count} items\n`;
    }
  });
  text += "\nItems:\n";
  tiles.forEach((tile, index) => {
    const clause = tile.clause ?? `Item ${index + 1}`;
    const impact = tile.impact && tile.impact !== "ALL" ? ` [${tile.impact}]` : "";
    text += `${index + 1}. ${clause}${impact}\n`;
  });
  return text;
}

function hasNoTiles(tiles: ReviewTileItem[], options?: UseTileCopyOptions): boolean {
  if (tiles.length === 0) {
    options?.onError?.("No tiles to copy");
    return true;
  }
  return false;
}

async function executeCopy(
  ctx: CopyContext,
  action: () => Promise<boolean>,
  successMsg: string,
  errorMsg: string,
  tileId?: string
): Promise<boolean> {
  if (tileId != null) ctx.setCopyingTileId(tileId);
  ctx.setIsCopying(true);
  try {
    const success = await action();
    if (success) ctx.options?.onSuccess?.(successMsg);
    else ctx.options?.onError?.(errorMsg);
    return success;
  } catch (error: unknown) {
    console.error("Copy error:", error);
    const msg = error instanceof Error ? error.message : "Unknown error";
    ctx.options?.onError?.(`${errorMsg}: ${msg}`);
    return false;
  } finally {
    if (tileId != null) ctx.setCopyingTileId(null);
    ctx.setIsCopying(false);
  }
}

export const useTileCopy = (options?: UseTileCopyOptions) => {
  const [isCopying, setIsCopying] = useState(false);
  const [copyingTileId, setCopyingTileId] = useState<string | null>(null);
  const { copyToClipboard, copyHtmlToClipboard } = useClipboard();
  const ctx: CopyContext = { setIsCopying, setCopyingTileId, options };

  const copySingleTile = async (tile: ReviewTileItem): Promise<boolean> => {
    const html = formatTileContentForCopy(tile);
    const msg = `Tile "${tile.clause ?? tile.id}" copied to clipboard`;
    return executeCopy(
      ctx,
      () => copyHtmlToClipboard(html, stripHtml(html), msg, true),
      msg,
      "Failed to copy tile",
      tile.id
    );
  };

  const copyMultipleTiles = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (hasNoTiles(tiles, options)) return false;
    const html = tiles.map(formatTileContentForCopy).join("<hr>");
    const msg = `${tiles.length} tiles copied to clipboard`;
    return executeCopy(
      ctx,
      () => copyHtmlToClipboard(html, stripHtml(html), msg, true),
      msg,
      "Failed to copy tiles"
    );
  };

  const copyTilesAsTable = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (hasNoTiles(tiles, options)) return false;
    const msg = `${tiles.length} tiles copied as table format`;
    return executeCopy(
      ctx,
      () => copyToClipboard(buildTilesTableText(tiles), msg, true),
      msg,
      "Failed to copy tiles as table"
    );
  };

  const copyTilesAsJSON = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (hasNoTiles(tiles, options)) return false;
    const msg = `${tiles.length} tiles copied as JSON`;
    return executeCopy(
      ctx,
      () => copyToClipboard(buildTilesJsonText(tiles), msg, true),
      msg,
      "Failed to copy tiles as JSON"
    );
  };

  const copyTilesSummary = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (hasNoTiles(tiles, options)) return false;
    const msg = `Summary of ${tiles.length} tiles copied to clipboard`;
    return executeCopy(
      ctx,
      () => copyToClipboard(buildTilesSummaryText(tiles), msg, true),
      msg,
      "Failed to copy tiles summary"
    );
  };

  return {
    isCopying,
    copyingTileId,
    copySingleTile,
    copyMultipleTiles,
    copyTilesAsTable,
    copyTilesAsJSON,
    copyTilesSummary,
  };
};
